/* ==========================================================================
   Zapatos Obi — product-form.js
   Selector de variantes (talla/color):
   - Botones por opción; al cambiar, localiza la variante correspondiente.
   - Actualiza precio, disponibilidad del botón, URL (?variant=) y galería.
   - Tacha valores sin variante disponible para la combinación actual.
   El submit del form lo intercepta cart.js (drawer AJAX).
   ========================================================================== */
(function () {
  'use strict';

  function formatMoney(cents, format) {
    var amount = (cents / 100).toFixed(2).replace('.', ',');
    var amountNoDecimals = Math.round(cents / 100).toString();
    return (format || '{{amount}}')
      .replace(/\{\{\s*amount\s*\}\}/, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, amountNoDecimals)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount);
  }

  class VariantSelector extends HTMLElement {
    connectedCallback() {
      var variantsEl = this.querySelector('[data-variants-json]');
      if (!variantsEl) return;

      try {
        this.variants = JSON.parse(variantsEl.textContent);
      } catch (e) {
        return;
      }

      var moneyEl = this.querySelector('[data-money-format]');
      try {
        this.moneyFormat = moneyEl ? JSON.parse(moneyEl.textContent) : '{{amount}}';
      } catch (e) {
        this.moneyFormat = '{{amount}}';
      }

      this.productUrl = this.getAttribute('data-url') || window.location.pathname;
      this.optionGroups = Array.prototype.slice.call(this.querySelectorAll('[data-option-group]'));
      this.variantInput = this.querySelector('[data-variant-id]');
      this.addButton = this.querySelector('[data-add-button]');
      this.addLabel = this.querySelector('[data-add-label]');
      /* puede haber varios (panel de info + barra fija móvil) */
      this.priceWrappers = Array.prototype.slice.call(this.querySelectorAll('[data-price-wrapper]'));

      this.strings = {
        add: (window.Obi && Obi.strings && Obi.strings.addToCart) || this._labelText(),
        soldOut: (window.Obi && Obi.strings && Obi.strings.soldOut) || 'Agotado',
        unavailable: (window.Obi && Obi.strings && Obi.strings.unavailable) || 'No disponible',
      };

      this.urgency = this.querySelector('[data-inventory-notice]');
      this.stockNotice = this.querySelector('[data-stock-notice]');
      var invEl = this.querySelector('[data-inventory-json]');
      try {
        this.inventory = invEl ? JSON.parse(invEl.textContent) : {};
      } catch (e) {
        this.inventory = {};
      }
      this.qtyInput = this.querySelector('.product__qty-input');
      this.skuWrapper = this.querySelector('[data-sku-wrapper]');
      this.skuEl = this.querySelector('[data-product-sku]');

      var self = this;
      this.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-option-btn]');
        if (btn) {
          self.selectOption(btn);
          return;
        }
        if (e.target.closest('[data-qty-minus]') && self.qtyInput) {
          self.qtyInput.value = Math.max(1, (parseInt(self.qtyInput.value, 10) || 1) - 1);
        }
        if (e.target.closest('[data-qty-plus]') && self.qtyInput) {
          var next = (parseInt(self.qtyInput.value, 10) || 1) + 1;
          if (self.qtyMax) next = Math.min(next, self.qtyMax);
          self.qtyInput.value = next;
        }
      });

      /* Cantidad escrita a mano: la mantenemos entre 1 y el stock disponible */
      if (this.qtyInput) {
        this.qtyInput.addEventListener('change', function () {
          var v = parseInt(self.qtyInput.value, 10) || 1;
          if (v < 1) v = 1;
          if (self.qtyMax && v > self.qtyMax) v = self.qtyMax;
          self.qtyInput.value = v;
        });
      }

      this.refreshAvailability();
      this.updateStock(this.findVariant(this.selectedOptions()));
    }

    updateInventory(variant) {
      if (!this.urgency) return;
      var threshold = parseInt(this.urgency.getAttribute('data-threshold'), 10) || 10;
      var qty = variant ? variant.inventory_quantity : null;
      var managed = variant && variant.inventory_management;
      var show = managed && typeof qty === 'number' && qty > 0 && qty <= threshold;

      this.urgency.hidden = !show;
      if (!show) return;

      var textEl = this.urgency.querySelector('[data-inventory-text]');
      var fillEl = this.urgency.querySelector('[data-inventory-fill]');
      var template = this.urgency.getAttribute('data-text-template') || '[N]';
      if (textEl) textEl.textContent = template.replace('[N]', qty);
      if (fillEl) fillEl.style.width = Math.round((qty / threshold) * 100) + '%';
    }

    /* Muestra el stock de la talla seleccionada y limita la cantidad al stock */
    updateStock(variant) {
      var info = variant && this.inventory ? this.inventory[variant.id] : null;
      var qty = info ? info.q : null;
      var policy = info ? info.p : null;

      if (this.stockNotice) {
        if (typeof qty !== 'number' || qty <= 0) {
          this.stockNotice.hidden = true;
        } else {
          var threshold = parseInt(this.stockNotice.getAttribute('data-threshold'), 10) || 10;
          var low = qty <= threshold;
          var tpl = low
            ? this.stockNotice.getAttribute('data-tpl-low')
            : this.stockNotice.getAttribute('data-tpl-count');
          this.stockNotice.textContent = (tpl || '[N]').replace('[N]', qty);
          this.stockNotice.classList.toggle('is-low', low);
          this.stockNotice.hidden = false;
        }
      }

      this.applyQtyMax(qty, policy);
    }

    /* Limita la cantidad al stock disponible. Solo si se controla el stock y NO
       se permite sobreventa (policy 'deny'); si no, sin límite. */
    applyQtyMax(qty, policy) {
      if (!this.qtyInput) return;
      var max = typeof qty === 'number' && policy === 'deny' ? Math.max(1, qty) : null;
      this.qtyMax = max;
      if (max) {
        this.qtyInput.max = max;
        if ((parseInt(this.qtyInput.value, 10) || 1) > max) this.qtyInput.value = max;
      } else {
        this.qtyInput.removeAttribute('max');
      }
    }

    _labelText() {
      return this.addLabel ? this.addLabel.textContent.trim() : 'Añadir al carrito';
    }

    selectedOptions() {
      return this.optionGroups.map(function (group) {
        var active = group.querySelector('[data-option-btn].is-selected');
        return active ? active.getAttribute('data-value') : null;
      });
    }

    selectOption(btn) {
      var group = btn.closest('[data-option-group]');
      group.querySelectorAll('[data-option-btn]').forEach(function (b) {
        b.classList.toggle('is-selected', b === btn);
        b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
      });
      var label = group.querySelector('[data-option-selected]');
      if (label) label.textContent = btn.getAttribute('data-value');

      this.onSelectionChange();
    }

    findVariant(options) {
      return this.variants.find(function (variant) {
        return options.every(function (value, i) {
          return value === null || variant.options[i] === value;
        });
      });
    }

    onSelectionChange() {
      var options = this.selectedOptions();
      var variant = this.variants.find(function (v) {
        return v.options.every(function (val, i) {
          return val === options[i];
        });
      });

      this.refreshAvailability();
      this.updateStock(variant);

      if (!variant) {
        this.setButtonState(false, this.strings.unavailable);
        return;
      }

      if (this.variantInput) this.variantInput.value = variant.id;

      /* Referencia (SKU) */
      if (this.skuEl) {
        this.skuEl.textContent = variant.sku || '';
        if (this.skuWrapper) this.skuWrapper.hidden = !variant.sku;
      }

      /* Precio (en todos los contenedores: panel + barra fija) */
      if (this.priceWrappers.length) {
        var html =
          '<div class="price' + (variant.compare_at_price > variant.price ? ' price--on-sale' : '') + '">' +
          '<span class="price__current">' + formatMoney(variant.price, this.moneyFormat) + '</span>';
        if (variant.compare_at_price > variant.price) {
          html += '<s class="price__compare">' + formatMoney(variant.compare_at_price, this.moneyFormat) + '</s>';
        }
        html += '</div>';
        this.priceWrappers.forEach(function (w) {
          w.innerHTML = html;
        });
      }

      /* Botón */
      if (variant.available) {
        this.setButtonState(true, this.strings.add);
      } else {
        this.setButtonState(false, this.strings.soldOut);
      }

      /* URL */
      try {
        var url = new URL(this.productUrl, window.location.origin);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      } catch (e) {}

      /* Galería: enfocar imagen de la variante (solo PC — en móvil la galería
         es vertical y el scrollIntoView pega un salto de página al tocar talla) */
      if (
        variant.featured_media &&
        variant.featured_media.id &&
        window.matchMedia('(min-width: 990px)').matches
      ) {
        var item = document.querySelector('[data-media-id="' + variant.featured_media.id + '"]');
        if (item) item.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }

    setButtonState(enabled, label) {
      if (this.addButton) this.addButton.disabled = !enabled;
      if (this.addLabel && label) this.addLabel.textContent = label;
    }

    /* Tacha los valores sin variante disponible dada la selección del resto */
    refreshAvailability() {
      var self = this;
      var selected = this.selectedOptions();

      this.optionGroups.forEach(function (group, groupIndex) {
        group.querySelectorAll('[data-option-btn]').forEach(function (btn) {
          var candidate = selected.slice();
          candidate[groupIndex] = btn.getAttribute('data-value');

          var match = self.variants.find(function (v) {
            return v.options.every(function (val, i) {
              return candidate[i] === null || val === candidate[i];
            });
          });

          var availableMatch = self.variants.some(function (v) {
            return (
              v.available &&
              v.options.every(function (val, i) {
                return candidate[i] === null || val === candidate[i];
              })
            );
          });

          btn.classList.toggle('is-unavailable', !availableMatch);
          btn.classList.toggle('is-nonexistent', !match);
        });
      });
    }
  }

  if (!customElements.get('variant-selector')) {
    customElements.define('variant-selector', VariantSelector);
  }
})();
